<template>
    <div 
      class="debug-panel" 
      v-if="isVisible" 
      v-show="session.rolesIncludes('admin')"
    >
      <!-- Toggle Switch -->
      <div class="toggle-container">
        <label class="toggle-switch">
          <input type="checkbox" v-model="session.debugEnabled" />
          <span class="slider round"></span>
        </label>
        <span class="toggle-label">Debug Mode</span>
        <button
          v-if="session.debugEnabled && sections.length"
          class="toggle-all-btn"
          @click="toggleAll"
        >{{ allExpanded ? 'Collapse All' : 'Expand All' }}</button>
      </div>
  
      <!-- Debug Information Section -->
      <div class="debug-content" v-if="session.debugEnabled" ref="debugContent">
        <!-- User Info Box -->
        <div class="info-box user-info">
          <h3>User Information</h3>
          <div class="info-field">
            <span class="field-label">User:</span>
            <span class="field-value">{{ debugData.user?.username || 'N/A' }}</span>
          </div>
          <div class="info-field">
            <span class="field-label">Email:</span>
            <span class="field-value">{{ debugData.user?.email || 'N/A' }}</span>
          </div>
          <div class="info-field">
            <span class="field-label">Group:</span>
            <span class="field-value">{{ debugData.user?.group || 'N/A' }}</span>
          </div>
        </div>
  
        <!-- Dynamic Debug Boxes -->
        <div
          v-for="log in sections"
          :key="log.id"
          class="info-box"
        >
          <div class="info-box-header" @click="toggleLog(log.id)">
            <span class="chevron">{{ expandedLogs.has(log.id) ? '▼' : '▶' }}</span>
            <h3 class="log-heading">{{ log.heading }}</h3>
            <span class="timestamp" v-if="log.timestamp">{{ new Date(log.timestamp).toLocaleString() }}</span>
          </div>
          <p v-if="!expandedLogs.has(log.id)" class="preview-text">{{ firstLine(log.content) }}</p>
          <p v-else class="debug-content-text">{{ log.content }}</p>
        </div>
      </div>
      <button
        v-if="session.debugEnabled && sections.length"
        class="scroll-bottom-btn"
        @click="scrollToBottom"
      >
        ↓ Bottom
      </button>
    </div>
  </template>
  
  <script>
  // import { ref, reactive, onMounted, watch } from 'vue';
  import debugApi from '@/services/debug_api';
  import { computed, ref, watch, nextTick } from 'vue';
  import { useSessionStore } from '@/stores/session';
  
  export default {
    name: 'DebugPanel',
    props: {
      isVisible: {
        type: Boolean,
        default: false
      }
    },
    setup() {
      const session = useSessionStore();
      // const debugEnabled = ref(false);

      const debugData = computed(() => ({
        user: {
          username: `${session.firstname || ''} ${session.lastname || ''}`.trim() || session.username || 'N/A',
          email: session.username || 'N/A',
          group: session.groupName || 'N/A',
        }
      }));

      // Instead of a local sections ref and watch, create a computed property
      const sections = computed(() => {
        return (session.debugLogs || []).map(log => ({
          id: log.id,
          heading: `Log #${log.id}`,
          content: log.content,
          timestamp: log.created_at || null,
        }));
      });

      const expandedLogs = ref(new Set());

      function toggleLog(id) {
        const next = new Set(expandedLogs.value);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        expandedLogs.value = next;
      }

      const allExpanded = computed(() =>
        sections.value.length > 0 &&
        sections.value.every(log => expandedLogs.value.has(log.id))
      );

      function toggleAll() {
        if (allExpanded.value) {
          expandedLogs.value = new Set();
        } else {
          expandedLogs.value = new Set(sections.value.map(log => log.id));
        }
      }

      function firstLine(content) {
        return (content?.split('\n')[0] ?? '').slice(0, 100);
      }

      const debugContent = ref(null);

      const scrollToBottom = () => {
        if (debugContent.value) {
          debugContent.value.scrollTop = debugContent.value.scrollHeight;
        }
      };

      // Watch for changes in the debug logs and scroll to bottom after the DOM updates
      watch(sections, async () => {
        await nextTick();
        scrollToBottom();
      });

      const toggleDebug = async () => {
        if (session.debugEnabled.value && session.rolesIncludes('admin')) {
          const logs = await debugApi.retrieveDebugLogs(
            session.currentSessionId,
            [],
            null
          );
          session.setDebugLogs(logs);
        }
      };

      return {
        session,
        debugData,
        sections,
        toggleDebug,
        debugContent,
        scrollToBottom,
        expandedLogs,
        toggleLog,
        firstLine,
        allExpanded,
        toggleAll,
      };
    }
  };
  </script>
  
  <style scoped>
  .debug-panel {
    width: 100%;
    height: calc(100vh - 20px);
    background-color: #f0f0f0;
    border-radius: 8px 0 0 8px;
    padding: 15px;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .toggle-container {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }

  .toggle-all-btn {
    margin-left: auto;
    padding: 3px 10px;
    font-size: 0.8em;
    background: #e8f0fb;
    border: 1px solid #1F6DCA;
    border-radius: 4px;
    cursor: pointer;
    color: #1F6DCA;
    white-space: nowrap;
  }

  .toggle-all-btn:hover {
    background-color: #1F6DCA;
    color: white;
  }
  
  .toggle-label {
    margin-left: 10px;
    font-weight: bold;
  }
  
  /* Toggle Switch Styling */
  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 30px;
  }
  
  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    border: 1px solid #ccc;
    transition: .4s;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 3px;
    background-color: #ccc;
    transition: .4s;
  }
  
  input:checked + .slider:before {
    background-color: #1F6DCA;
  }
  
  input:checked + .slider {
    background-color: white;
  }
  
  input:focus + .slider {
    box-shadow: 0 0 1px #1F6DCA;
  }
  
  input:checked + .slider:before {
    transform: translateX(28px);
  }
  
  .slider.round {
    border-radius: 34px;
  }
  
  .slider.round:before {
    border-radius: 50%;
  }
  
  /* Content Styling */
  .debug-content {
    overflow-y: auto;
    flex-grow: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding-right: 5px;
  }
  
  .info-box {
    background-color: white;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  }
  
  .info-box h3 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 16px;
    color: #333;
  }
  
  .info-field {
    display: flex;
    margin-bottom: 8px;
  }
  
  .field-label {
    font-weight: bold;
    width: 80px;
    flex-shrink: 0;
  }
  
  .field-value {
    flex-grow: 1;
  }
  
  .debug-list {
    margin: 0;
    padding-left: 20px;
  }
  
  .debug-list li {
    margin-bottom: 5px;
  }

  /* Scrollbar styling */
  .debug-content::-webkit-scrollbar {
    width: 6px;
  }
  
  .debug-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .debug-content::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }
  
  .debug-content::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  .info-box-header {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .chevron {
    font-size: 0.75em;
    flex-shrink: 0;
    color: #666;
  }

  .log-heading {
    font-size: 1.1em;
    font-weight: bold;
    margin: 0;
    flex-shrink: 0;
  }

  .timestamp {
    margin-left: auto;
    font-size: 0.8em;
    color: #666;
    white-space: nowrap;
  }

  .preview-text {
    margin: 6px 0 0;
    color: #999;
    font-size: 0.85em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .debug-content-text {
    margin: 6px 0 0;
    max-height: 300px;
    overflow-y: auto;
    text-align: left;
    white-space: pre-wrap;
    font-family: monospace;
  }

  .scroll-bottom-btn {
    flex-shrink: 0;
    align-self: flex-end;
    margin-top: 8px;
    padding: 4px 12px;
    font-size: 0.8em;
    background: #e8f0fb;
    border: 1px solid #1F6DCA;
    border-radius: 4px;
    cursor: pointer;
    color: #1F6DCA;
  }
  .scroll-bottom-btn:hover {
    background-color: #1F6DCA;
    color: white;
  }
  </style>